import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, clearToken, getToken, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
    const [loading, setLoading] = useState(true);

    const refreshMe = useCallback(async () => {
        // CRITICAL: If returning from Google OAuth callback (session_id in URL
        // hash), let AuthCallback exchange it first — do not attempt /auth/me
        // yet as there is no JWT stored on the initial render.
        if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
            setLoading(false);
            return;
        }
        const token = getToken();
        if (!token) {
            setUser(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            clearToken();
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshMe();
    }, [refreshMe]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setToken(data.access_token);
            setUser(data.user);
            try { localStorage.setItem("ss_user_cache", JSON.stringify(data.user)); } catch (e) {}
            return { ok: true, user: data.user };
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            return { ok: false, error: msg };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // ignore
        }
        clearToken();
        try { localStorage.removeItem("ss_user_cache"); } catch (e) {}
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refreshMe }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
